import { useAtom, useSetAtom } from "jotai";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";
import { UserType, toSlug } from "common";
import { notify } from "../util/notify";
import { forgetPushSubscription } from "../util/push";
import { callApi } from "../api/client";
import {
  AuthFailure,
  getIdToken,
  signInWithPassword,
  signOutUser,
  confirmPasswordReset,
} from "../auth/cognito";
import {
  PROFILE_STORAGE_KEY,
  initialUserState,
  reportsPerFacilityPayerState,
  userState,
} from "./atoms";

export { initialUserState } from "./atoms";
export const GLOBAL_TOAST_ID = "global-toast-id";

const GENERIC_ERROR = "Oops, there was an issue.";
const isAuthenticationError = (error: any) =>
  error?.response?.data?.description === "Authentication Error";
const errorMessage = (error: any) =>
  error instanceof AuthFailure
    ? error.message
    : error?.response?.data?.message || GENERIC_ERROR;

/** Schema validation errors arrive as a JSON list of problems; hand-written ones are plain sentences */
const formatValidationError = (message: string) => {
  try {
    const problems = JSON.parse(message);
    if (Array.isArray(problems)) {
      return problems.map(({ instancePath, message }: any) => `${instancePath.substring(1)}: ${message}`).join("\n");
    }
  } catch {
    // not JSON
  }
  return message;
};

// The first useUser() to mount per page load verifies the stored session once
let sessionCheckedOnLoad = false;

export const useUser = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useAtom(userState);
  const setReports = useSetAtom(reportsPerFacilityPayerState);
  const { group, facility, payer, residentName, billType, index } = params;

  // Cached report data belongs to the user who loaded it, so it goes away with the session
  const clearSession = () => {
    setUser(initialUserState);
    setReports({});
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    localStorage.removeItem("reportsPerFacilityPayerState");
  };

  const redirectToLogin = () => {
    const next = location.pathname.includes("login")
      ? `/${params.group}`
      : `${location.pathname}${location.search}`;
    navigate(`/${toSlug(params.group)}/login?next=${encodeURIComponent(next)}`);
  };

  const endSession = () => {
    clearSession();
    redirectToLogin();
  };

  useEffect(() => {
    if (location.pathname === "/") {
      window.location.href = "/fairview-and-co";
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      user?.username &&
      !sessionCheckedOnLoad &&
      !location.pathname.includes("login") &&
      !location.pathname.includes("reset-password")
    ) {
      sessionCheckedOnLoad = true;
      (async () => {
        const authToken = await getIdToken();
        if (!authToken) return endSession();
        try {
          // Also picks up permission changes made by an admin since the last visit
          setUser(await callApi("/getProfile", { authToken }));
        } catch (error) {
          if (isAuthenticationError(error)) endSession();
        }
      })();
    }
    // The session is checked once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invokeApiImpl = async ({
    endpoint,
    data,
    authTokenOverride,
    isRetry,
    onUploadProgress,
    silent,
  }: any): Promise<any> => {
    // Background requests (polling) must not flash a toast or nag about transient failures
    const toastId = silent ? undefined : notify.loading("Loading...", { id: GLOBAL_TOAST_ID });

    try {
      const authToken = authTokenOverride ?? (await getIdToken());
      const body = {
        authToken,
        group,
        facility,
        payer,
        residentName,
        billType,
        index: index !== undefined ? Number(index) : index,
        ...data,
      };
      return await (onUploadProgress ? callApi(endpoint, body, { onUploadProgress }) : callApi(endpoint, body));
    } catch (error: any) {
      console.error(error?.response?.data?.description || error);
      console.error(error?.response?.data?.message);

      const description = error?.response?.data?.description;
      if (description === "Authentication Error") {
        if (!isRetry) {
          // The access token may simply have been rejected as stale: one forced refresh, one retry
          const freshToken = await getIdToken({ forceRefresh: true });
          if (freshToken) {
            if (toastId) notify.dismiss(toastId);
            return await invokeApiImpl({
              endpoint,
              data,
              authTokenOverride: freshToken,
              isRetry: true,
              onUploadProgress,
              silent,
            });
          }
        }
        clearSession();
        notify.error("Please log in", { id: GLOBAL_TOAST_ID });
        redirectToLogin();
      } else if (silent) {
        // Failures of background requests are handled by their caller
      } else if (description === "Custom Error") {
        notify.error(error?.response?.data?.message, { duration: 5000 });
      } else if (description === "Validation Error") {
        notify.error(formatValidationError(error.response.data.message), { style: { whiteSpace: "pre-line" } });
      } else {
        notify.error(error?.response?.data?.description || GENERIC_ERROR);
      }
      throw error;
    } finally {
      if (toastId) notify.dismiss(toastId);
    }
  };

  // Stable identity across renders: components list invokeApi in effect dependencies, so a new function each
  // render would re-run those effects forever. The ref always points at the latest render's closure.
  const invokeApiRef = useRef(invokeApiImpl);
  invokeApiRef.current = invokeApiImpl;
  const invokeApi: (args: any) => Promise<any> = useCallback(
    (args: any) => invokeApiRef.current(args),
    []
  );

  /** Loads the profile for the session that was just established; a different group's user is turned away */
  const completeSignIn = async (expectedGroup?: string) => {
    const authToken = await getIdToken();
    const profile = await callApi("/getProfile", { authToken });
    if (expectedGroup && profile.group !== expectedGroup) {
      await signOutUser();
      throw new AuthFailure("Invalid username or password");
    }
    setUser(profile);
    return profile;
  };

  const login = async (username: string, password: string) => {
    notify.loading("Loading...", { id: GLOBAL_TOAST_ID });
    try {
      await signInWithPassword(username, password);
      const profile = await completeSignIn(params.group);
      notify.success("You have successfully logged in!", { id: GLOBAL_TOAST_ID });
      if (location.pathname.includes("login")) {
        navigate(searchParams.get("next") || `/${toSlug(profile.group)}`);
      }
    } catch (error) {
      notify.dismiss(GLOBAL_TOAST_ID);
      notify.error(errorMessage(error), { duration: 5000 });
    }
  };

  const resetPassword = async (
    token: string,
    password: string,
    username: string
  ) => {
    notify.loading("Loading...", { id: GLOBAL_TOAST_ID });
    try {
      await confirmPasswordReset({ username, code: token, newPassword: password });
      await signInWithPassword(username, password);
      const profile = await completeSignIn();
      notify.success("You have successfully logged in!", { id: GLOBAL_TOAST_ID });
      navigate(`/${toSlug(profile.group)}`);
    } catch (error) {
      notify.dismiss(GLOBAL_TOAST_ID);
      notify.error(errorMessage(error), { duration: 5000 });
    }
  };

  const logout = async () => {
    // Before the session ends: the server needs the token to know whose subscription to drop. Bounded, so a slow network cannot block signing out.
    await Promise.race([forgetPushSubscription(invokeApi), new Promise((resolve) => setTimeout(resolve, 2000))]);
    notify.success("You have successfully logged out");
    navigate(`/${toSlug(user.group)}/login`);
    clearSession();
    await signOutUser();
  };

  return {
    login,
    logout,
    resetPassword,
    invokeApi,
    setUser,
    group,
    facility,
    payer: payer?.toUpperCase(),
    authenticated: user?.username,
    isAdmin: user?.type === UserType.Admin,
    user,
    navigate,
    ...user,
  };
};
